import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, fmtErr } from "../lib/api";
import { Button } from "./ui/button";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

const RankList = ({ title, rows, label, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="panel min-w-0 p-4" data-testid={testid}>
      <p className="eyebrow mb-3">{title}</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="text-sm" data-testid={`${testid}-row-${i}`}>
            <div className="mb-1 flex justify-between gap-3"><span className="min-w-0 truncate">{label(r)}</span><span className="font-mono text-xs">{r.count.toLocaleString()}</span></div>
            <div className="h-1.5 rounded bg-slate-800"><div className="h-1.5 rounded bg-rose-600 transition-[width] duration-500" style={{ width: `${(r.count * 100) / max}%` }} /></div>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-slate-500">No faults in this range.</p>}
      </div>
    </div>
  );
};

const Matrix = ({ m }) => {
  const max = Math.max(1, ...m.cells.flat());
  if (!m.plants.length) return null;
  return (
    <div className="panel overflow-x-auto p-4" data-testid="trends-matrix">
      <p className="eyebrow mb-3">Plant x fault code (top 10 each)</p>
      <table className="text-xs">
        <thead><tr><th className="px-2 py-1 text-left">Plant</th>{m.codes.map((c) => <th key={c} className="px-2 py-1 font-mono">{c}</th>)}</tr></thead>
        <tbody>
          {m.plants.map((p, i) => (
            <tr key={p}>
              <td className="px-2 py-1 font-mono text-cyan-300">{p}</td>
              {m.cells[i].map((v, j) => <td key={j} className="px-2 py-1 text-center" style={{ backgroundColor: v ? `rgba(212,51,59,${0.12 + (0.75 * v) / max})` : undefined }}>{v || ""}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AsrsTrends = () => {
  const [f, setF] = useState({ from: "", to: "", plant: "" });
  const [d, setD] = useState(null);
  const params = { from: f.from || undefined, to: f.to || undefined, plant: f.plant || undefined };
  useEffect(() => {
    api.get("/asrs/trends", { params }).then((r) => setD(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [f.from, f.to, f.plant]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = async () => {
    try {
      const r = await api.get("/asrs/trends/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asrs_fault_trends_${d.from}_${d.to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not create the PDF"); }
  };
  return (
    <div className="space-y-4" data-testid="asrs-trends">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-400">From<input type="month" className="field mt-1 h-10 w-40" value={f.from || d?.from || ""} onChange={(e) => setF({ ...f, from: e.target.value })} data-testid="trends-from" /></label>
        <label className="text-xs text-slate-400">To<input type="month" className="field mt-1 h-10 w-40" value={f.to || d?.to || ""} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="trends-to" /></label>
        <label className="text-xs text-slate-400">Plant<input className="field mt-1 h-10 w-32" placeholder="All, e.g. H08" value={f.plant} onChange={(e) => setF({ ...f, plant: e.target.value.toUpperCase() })} data-testid="trends-plant" /></label>
        {(f.from || f.to || f.plant) && <Button variant="ghost" onClick={() => setF({ from: "", to: "", plant: "" })} data-testid="trends-reset">Last 12 months</Button>}
        <div className="flex-1" />
        <Button onClick={pdf} disabled={!d} className="tap bg-rose-600 hover:bg-rose-700" data-testid="trends-download-pdf"><Download className="mr-1 h-4 w-4" />Download PDF</Button>
      </div>
      {d && (
        <>
          <p className="text-sm text-slate-400" data-testid="trends-total"><span className="font-display text-2xl font-extrabold text-slate-100">{d.total.toLocaleString()}</span> faults {d.from} to {d.to} - {d.sources.Quickbase.toLocaleString()} from Quickbase, {d.sources.App} raised in app{d.uncoded ? ` - ${d.uncoded.toLocaleString()} without a fault code` : ""}</p>
          <div className="panel h-64 p-4" data-testid="trends-monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.months.map((m) => ({ ...m, label: monthLabel(m.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--c-slate-800))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "rgb(var(--c-slate-400))" }} />
                <YAxis tick={{ fontSize: 11, fill: "rgb(var(--c-slate-400))" }} width={40} />
                <Tooltip cursor={{ fill: "rgba(212,51,59,0.08)" }} contentStyle={{ background: "rgb(var(--ink-2))", border: "1px solid rgb(var(--c-slate-700))", fontSize: 12 }} />
                <Bar dataKey="count" name="Faults" fill="#D4333B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <RankList title="Top 10 fault codes" rows={d.codes} label={(r) => <><span className="mr-2 font-mono text-xs text-rose-400">{r.code}</span>{r.label}</>} testid="trends-codes" />
            <RankList title="Top 10 cranes / plant" rows={d.plants} label={(r) => <span className="font-mono">{r.plant}</span>} testid="trends-plants" />
          </div>
          <Matrix m={d.matrix} />
        </>
      )}
    </div>
  );
};
